import React, {
  forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback
} from 'react';
import { AxiosPromise } from 'axios';
import { ServerResponse } from 'data/types'
import { FormTemplate, FormTemplateSection, FormTemplateValidator } from 'data/template/template.types';
import Form, { FieldUpdateHandlerParam, CustomValidationFunction } from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import SectionLabel from 'components/SectionLabel';
import CommonGrid from 'shared/commonGrid';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from 'i18n/i18n.types';
import {
  Label,
} from '@blueprintjs/core';
import {
  SelectionChangedEvent, ColDef,
  GridReadyEvent,
  GetRowIdParams,
  IServerSideGetRowsParams,
  RowNode,
  SortModelItem,
  RowSelectedEvent,
  RowDataChangedEvent
} from '@ag-grid-enterprise/all-modules';
import ServerSideDatasource from 'shared/commonGrid/serverSideDatasource';
import { AgGridReact } from '@ag-grid-community/react';
import {
  AdaptedAlarmNotificationProfile, AlarmsDef, AlarmsInst, InterceptAlarm
} from 'data/alarmNotificationProfile/alarmNotificationProfile.types';
import cloneDeep from 'lodash.clonedeep';

type Props = {
  template: FormTemplate;
  data: Partial<AdaptedAlarmNotificationProfile>;
  i18nKeyPrefix?: string;
  isEditing?: boolean;
  onFieldUpdate?: (param: FieldUpdateHandlerParam, formSection?: 'main') => void;
  onFieldCreateNew?: (param: FieldUpdateHandlerParam) => void,
  onSelectionChanged?: (event: RowSelectedEvent) => void,
  gridData?: any[],
  profileType?: string,
  getAlarmIntercept: (body: string) => AxiosPromise<ServerResponse<InterceptAlarm>>
  getAlarmInstace?: (body: string) => AxiosPromise<ServerResponse<AlarmsInst>>
  alarmDefData?: AlarmsDef[],
};

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => AdaptedAlarmNotificationProfile;
};

export const ALARM_NOTIFICATION_PROFILE_FORM_I18N_KEY =
  'alarmNotificationProfile.form';

export const AlarmNotificationProfileForm = forwardRef<Ref, Props>(
  (
    {
      template,
      data,
      i18nKeyPrefix = ALARM_NOTIFICATION_PROFILE_FORM_I18N_KEY,
      onFieldCreateNew = () => null,
      onFieldUpdate = () => null,
      onSelectionChanged = () => null,
      isEditing,
      gridData,
      profileType = '',
      getAlarmIntercept,
      getAlarmInstace,
      alarmDefData
    }: Props,
    ref,
  ) => {
    const gridRef = useRef<any>(null);
    const { t } = useTranslation();
    const getSelectedRowIdsFromData = () => {
      if (!data) {
        return new Set<string>();
      }
      switch (profileType) {
        case 'ALARM_DEFINITION':
          const alarmDefList: string[] = data.alarmsDef?.map((alarmDef) => alarmDef.id || '') || [];
          return new Set(alarmDefList);
        case 'ALARM_INSTANCE':
          const alarmInstList: string[] = data.alarmsInst?.map((alarmInst) => alarmInst.id || '') || [];
          return new Set(alarmInstList);
        case 'INTERCEPT_ALARM':
          const interceptAlarmList: string[] = data.interceptAlarm?.map((interceptAlarm) => interceptAlarm.id || '') || [];
          return new Set(interceptAlarmList);
        default:
          return new Set<string>();
      }
    }
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(getSelectedRowIdsFromData());
    const [rowSelectedMessage, setRowSelectedMessage] = useState<string>('')
    const defaultColDef = {
      flex: 1,
      resizable: true,
      filter: 'agTextColumnFilter',
      sortable: true,
      floatingFilter: true,
    };

    const alarmsDefColumnDefs: ColDef[] = [
      {
        checkboxSelection: true,
        width: 70
      },
      {
        field: 'alarmId',
        headerName: 'Alarm Id',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
      },
      {
        field: 'alarmGrpName',
        headerName: 'Alarm Grpid',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
      },
      {
        field: 'moduleName',
        headerName: 'Module',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
      },
      {
        field: 'text',
        headerName: 'Description',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 250,
      }
    ];

    const alarmsInstColumnDefs: ColDef[] = [
      {
        checkboxSelection: true,
        width: 70
      },
      {
        field: 'alarmid',
        headerName: 'Alarm Id',
        filter: 'agTextColumnFilter',
        resizable: true,
      },
      {
        field: 'ss8group',
        headerName: 'Group',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
      },
      {
        field: 'module',
        headerName: 'Module',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
      },
      {
        field: 'instance',
        headerName: 'Instance',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
      },
      {
        field: 'timestamp',
        resizable: true,
        width: 150,
        valueGetter: (p) => {
          return `${p.data.date} ${p.data.time}`;
        }
      },
      {
        field: 'text',
        headerName: 'Description',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        flex: 1,
        minWidth: 250,
      }
    ];

    const interceptAlarmColumnDefs: ColDef[] = [
      {
        checkboxSelection: true,
        width: 70
      },
      {
        field: 'survIndex',
        headerName: 'Surveilance Index',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        hide: true
      },
      {
        field: 'coId',
        headerName: 'Co Id',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 165,
      },
      {
        field: 'caseId',
        headerName: 'Case Id',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 165,
      }
    ];

    const updateRowSelectedMessage = (selectedRowIdList: Set<string>) => {
      const message = `${t(`${i18nKeyPrefix}.grid.hint` as TranslationKey)} (${selectedRowIdList.size}) : ${Array.from(selectedRowIdList).join(', ')}`;
      return message
    }

    useEffect(() => {
      // on data change, then update the selected Row
      setSelectedRowIds(getSelectedRowIdsFromData());
    }, [data])

    useEffect(() => {
      setRowSelectedMessage(updateRowSelectedMessage(selectedRowIds))
    }, [selectedRowIds])

    const mainRef = useRef<Form>(null);

    const customFieldComponentMap = {
      privateKey: SectionLabel,
      certificate: SectionLabel,
    };

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isMainValid =
        mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
      return isMainValid;
    };

    const getValues = () => {
      const formValues: AdaptedAlarmNotificationProfile = {
        ...mainRef.current?.getValues(),
      };
      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues,
    }));

    const onMainFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'main');

    const handleGetAlarmInterceptFromServer = () => {
      return {
          getRows(params: IServerSideGetRowsParams) {
            const { request } = params;
            const updateSortModel = request.sortModel.reduce<SortModelItem[]>((result, sortModel) => {
              result.push(sortModel);
              return result;
            }, [])
            request.sortModel = updateSortModel;
            const dataSource = new ServerSideDatasource({});
            const options = dataSource.buildParamsFromJsonForXREST(request);
            getAlarmIntercept(options)
              .then((r) => {
                const rowData = r.data?.data? r.data?.data.map((item) => {
                  return {
                    ...item,
                    id: item.survIndex
                  }
                }) : [];
                const rowCount = r.data?.metadata?.recordsInView || 0;
                params.success({ rowData, rowCount });
              })
              .catch((r) => {
                params.fail();
              });
          }
        };
    }

    const handleGetAlarmInstanceFromServer = () => {
      return {
          getRows(params: IServerSideGetRowsParams) {
            const { request } = params;
            const updateSortModel = request.sortModel.reduce<SortModelItem[]>((result, sortModel) => {
              if (sortModel.colId === 'timestamp') {
                result.push(
                  { colId: 'date', sort: sortModel.sort },
                  { colId: 'time', sort: sortModel.sort }
                )
              } else {
                result.push(sortModel);
              }
              return result;
            }, [])
            request.sortModel = updateSortModel;
            const dataSource = new ServerSideDatasource({});
            const options = dataSource.buildParamsFromJsonForXREST(request);
            if (getAlarmInstace) {
              getAlarmInstace(options)
              .then((r) => {
                const rowData = r.data?.data? r.data?.data.map((item) => {
                  return {
                    ...item,
                    id: `${item.alarmid}|${item.instance}`
                  }
                }) : [];
                const rowCount = r.data?.metadata?.recordsInView || 0;
                params.success({ rowData, rowCount });
              })
              .catch((r) => {
                params.fail();
              });
            }
          }
        };
    }

    const onGridReady = useCallback((grid: GridReadyEvent) => {
      if (profileType === 'ALARM_INSTANCE') {
        grid.columnApi.applyColumnState({
          state: [{ colId: 'timestamp', sort: 'desc', sortIndex: 0 }],
          defaultState: { sort: null },
        })
        grid.api.setServerSideDatasource(handleGetAlarmInstanceFromServer());
      }
      if (profileType === 'INTERCEPT_ALARM') {
        grid.api.setServerSideDatasource(handleGetAlarmInterceptFromServer());
      }
    }, [profileType]);

    const getRowIdForAlarmIntercept = (params: GetRowIdParams) => {
      return `${params.data.survIndex}`;
    }
    const getRowIdForAlarmInst = (params: GetRowIdParams) => {
      return `${params.data.alarmId}|${params.data.instance}`;
    }
    const getRowIdForAlarmDef = (params: GetRowIdParams) => {
      return `${params.data.alarmId}`;
    }

    const handleRowSelectionChanged = (event: RowSelectedEvent) => {
      const allRowIds = cloneDeep(selectedRowIds)
      const isSelected = event.node.isSelected();
      if (isSelected) {
        allRowIds.add(event.data.id)
      } else {
        allRowIds.delete(event.data.id)
      }
      setSelectedRowIds(allRowIds)
      onSelectionChanged(event);
    }

    const emailsLimitSizeValidator = useCallback<CustomValidationFunction>((
      _propFields?: unknown,
      _stateFields?: unknown,
      _value?: string,
      _lastSavedValue?: string,
      validator?: FormTemplateValidator,
      form?: Form | undefined,
    ) => {
      let valid = true;
      let message = '';
      const formValues = getValues();
      if (formValues) {
        console.log(formValues);
        if (formValues.email?.length > 10) {
          valid = false;
          message = t(`${i18nKeyPrefix}.fieldError.${validator?.customValidationFunction}` as TranslationKey) ?? '';
        }
      }
      return { isValid: valid, errorMessage: message };
    }, [i18nKeyPrefix, t])

    const handlePageDataChanged = (event: RowDataChangedEvent) => {
      event.api.forEachNode((node) => {
        if (node.data && selectedRowIds.has(node.data.id)) {
          node.setSelected(true);
        }
      });
    }

    const customValidationFunctionMap = {
      emailsLimitSizeValidator
    }

    const getColumDefForView = (colDef: ColDef[]) => {
      const clonnedColDef = cloneDeep(colDef)
      if (!isEditing) {
        clonnedColDef.splice(0, 1);
      }
      return clonnedColDef;
    }
    return (
      <div
        className="alarm-notification-profile-form"
        data-testid="AlarmNotificationProfileForm"
      >
        <div data-testid="AlarmNotificationProfileFormMain">
          <FormLayout>
            <Form
              ref={mainRef}
              name={template.formId ?? template.key}
              defaults={data}
              fields={(template.main as FormTemplateSection).fields}
              layout={
                (template.main as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={onMainFieldUpdate}
              fieldCreateNewHandler={onFieldCreateNew}
              customFieldComponentMap={customFieldComponentMap}
              customValidationFunctionMap={customValidationFunctionMap}
            />
            <div><Label className="field-hint-message"> {rowSelectedMessage} </Label></div>
            <div style={{ height: '560px', width: '100%' }} data-testid="AlarmNotificationProfileFormGrid" className="ag-theme-balham common-grid-container">
              {(gridData && profileType === 'ALARM_DEFINITION')? (
                <AgGridReact
                  ref={gridRef}
                  columnDefs={getColumDefForView(alarmsDefColumnDefs)}
                  pagination
                  paginationPageSize={100}
                  rowSelection="multiple"
                  onGridReady={onGridReady}
                  suppressRowClickSelection
                  onRowSelected={handleRowSelectionChanged}
                  rowData={alarmDefData}
                  getRowId={getRowIdForAlarmDef}
                  onModelUpdated={handlePageDataChanged}
                />
              ) : null}
              {(gridData && profileType === 'ALARM_INSTANCE')? (
                <AgGridReact
                  ref={gridRef}
                  columnDefs={getColumDefForView(alarmsInstColumnDefs)}
                  pagination
                  paginationPageSize={100}
                  rowSelection="multiple"
                  rowModelType="serverSide"
                  serverSideStoreType="partial"
                  cacheBlockSize={100}
                  onGridReady={onGridReady}
                  suppressRowClickSelection
                  onRowSelected={handleRowSelectionChanged}
                  getRowId={getRowIdForAlarmInst}
                  onModelUpdated={handlePageDataChanged}
                />
              ) : null}
              {(gridData && profileType === 'INTERCEPT_ALARM')? (
                <AgGridReact
                  ref={gridRef}
                  columnDefs={getColumDefForView(interceptAlarmColumnDefs)}
                  pagination
                  paginationPageSize={100}
                  rowSelection="multiple"
                  rowModelType="serverSide"
                  serverSideStoreType="partial"
                  cacheBlockSize={100}
                  onGridReady={onGridReady}
                  suppressRowClickSelection
                  getRowId={getRowIdForAlarmIntercept}
                  onRowSelected={handleRowSelectionChanged}
                  onModelUpdated={handlePageDataChanged}
                />
              ) : null}
            </div>
          </FormLayout>
        </div>
      </div>
    );
  },
);

export default AlarmNotificationProfileForm;
